import {
  Box,
  Button,
  Divider,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import CustomModal from '../../../components/General/CustomModal';
import GoogleMaps from '../../../components/Layout/Google/GoogleMaps';
import MapWrapper from '../../../components/Layout/Google/MapWrapper';
import { ReduxState } from '../../../types';
import apiClient from '../../../requests/api/apiClient';
import axios from '../../../utils/axios';
import useNodes from '../../../hooks/useNodes';
import useSensors from '../../../hooks/useSensors';
import { filteredDevices } from '../Layout/helpers';

interface AddWeatherStationModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (sensorModelId: string) => void;
}

type Coord = { lng: number; lat: number };

const AddWeatherStationModal: React.FC<AddWeatherStationModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { selectedFarm } = useSelector(
    (state: ReduxState) => state.accountFarm,
  );
  const { sensorNodeFilter } = useSelector(
    (state: ReduxState) => state.farmSector,
  );

  const [step, setStep] = useState<1 | 2>(1);
  const [name, setName] = useState('');
  const [softlandBrand, setSoftlandBrand] = useState('');
  const [softlandModel, setSoftlandModel] = useState('');
  const [nodeId, setNodeId] = useState('');
  const [telemetryType, setTelemetryType] = useState('wiseconn');
  const [zoneId, setZoneId] = useState('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [coordinates, setCoordinates] = useState<Coord[] | null>(
    null,
  );

  const [sensorBrands, setSensorBrands] = useState<string[]>([]);
  const [sensorModelsList, setSensorModelsList] = useState<string[]>(
    [],
  );

  const { nodes } = useNodes(selectedFarm?.id ?? '');
  const { sensors } = useSensors(selectedFarm?.id ?? '');
  const allFarmDevices = [...nodes, ...sensors];

  useEffect(() => {
    if (!open) return;
    const fetchData = async () => {
      const modelsRes = await apiClient.devices
        .softlandGetDeviceModelsList();
      setSensorBrands(modelsRes.data?.sensorBrands ?? []);
      setSensorModelsList(modelsRes.data?.sensorModels ?? []);
    };
    fetchData();
  }, [open]);

  const farmCoordinates = selectedFarm?.coordinates
    ? selectedFarm.coordinates.map(
      ({ lat, lng }) => ({
        lat: typeof lat === 'string' ? parseFloat(lat) : lat,
        lng: typeof lng === 'string' ? parseFloat(lng) : lng,
      }),
    ) : null;

  const resetForm = () => {
    setStep(1);
    setName('');
    setSoftlandBrand('');
    setSoftlandModel('');
    setNodeId('');
    setTelemetryType('wiseconn');
    setZoneId('');
    setComment('');
    setCoordinates(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleNextStep = () => {
    if (!name.trim()) {
      enqueueSnackbar(
        'Debes proporcionar un nombre',
        { variant: 'error' },
      );
      return;
    }
    if (!zoneId.trim()) {
      enqueueSnackbar(
        'Debes proporcionar un Zone ID',
        { variant: 'error' },
      );
      return;
    }
    if (!nodeId) {
      enqueueSnackbar(
        'Debes seleccionar un nodo',
        { variant: 'error' },
      );
      return;
    }
    setStep(2);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const dataToSend: Record<string, string | boolean> = {
        name,
        deviceType: 'sensor',
        sensorType: 'clima',
        isWeatherStation: true,
        zoneId,
        nodeId,
        telemetryType,
      };
      if (softlandBrand) dataToSend.softlandBrand = softlandBrand;
      if (softlandModel) dataToSend.softlandModel = softlandModel;
      if (comment) dataToSend.comment = comment;

      const formData = new FormData();
      formData.append('data', JSON.stringify({
        ...dataToSend,
        farmId: selectedFarm?.id ?? '',
        coordinates: coordinates ? coordinates[0] : null,
      }));

      const { data } = await axios.post('/api/sensors', formData, {
        headers: { 'content-type': 'multipart/form-data' },
      });
      enqueueSnackbar(
        data.message || 'Estación creada exitosamente',
        { variant: 'success' },
      );
      const newSensorModelId = data.sensor?.sensorModelId;
      resetForm();
      onSuccess(newSensorModelId);
    } catch (err) {
      const { response } = err as AxiosError;
      if (response?.data) {
        enqueueSnackbar(
          (response.data as { message: string }).message,
          { variant: 'error' },
        );
      } else {
        enqueueSnackbar(
          (err as Error).message,
          { variant: 'error' },
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const isMapStep = step === 2;

  return (
    <CustomModal
      open={open}
      handleClose={handleClose as any}
      cardClassName={isMapStep ? undefined : undefined}
    >
      <Box
        display="flex"
        flexDirection="column"
        flex="1"
        minWidth={isMapStep ? '70vw' : '500px'}
        minHeight={isMapStep ? '70vh' : undefined}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Typography variant="h3" color="textPrimary">
            <span style={{
              borderBottom: '3px solid #5b9bd5',
              paddingBottom: '4px',
            }}>
              Agregar Estación
            </span>
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {step === 1 && (
          <>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                label="Nombre de la estación de clima"
                variant="standard"
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <Box display="flex" gap={2}>
                <TextField
                  label="Marca (Opcional)"
                  variant="standard"
                  select
                  fullWidth
                  value={softlandBrand}
                  onChange={(e) => setSoftlandBrand(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Ninguna</em>
                  </MenuItem>
                  {sensorBrands.map((brand) => (
                    <MenuItem key={brand} value={brand}>
                      {brand}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Modelo - nombre (opcional)"
                  variant="standard"
                  select
                  fullWidth
                  value={softlandModel}
                  onChange={(e) => setSoftlandModel(
                    e.target.value,
                  )}
                >
                  <MenuItem value="">
                    <em>Ninguno</em>
                  </MenuItem>
                  {sensorModelsList.map((model) => (
                    <MenuItem key={model} value={model}>
                      {model}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box display="flex" gap={2}>
                <TextField
                  label="Nodo"
                  variant="standard"
                  select
                  fullWidth
                  value={nodeId}
                  onChange={(e) => setNodeId(e.target.value)}
                >
                  {nodes.length > 0
                    ? nodes.map((node: any) => (
                      <MenuItem key={node.id} value={node.id}>
                        {node.name}
                      </MenuItem>
                    ))
                    : <MenuItem disabled>No hay nodos</MenuItem>
                  }
                </TextField>
                <TextField
                  label="Telemetría"
                  variant="standard"
                  select
                  fullWidth
                  value={telemetryType}
                  onChange={(e) => setTelemetryType(
                    e.target.value,
                  )}
                >
                  <MenuItem value="wiseconn">Wiseconn</MenuItem>
                  <MenuItem value="sn">Helius</MenuItem>
                </TextField>
              </Box>
              <TextField
                label="Zone ID"
                variant="standard"
                fullWidth
                value={zoneId}
                onChange={(e) => setZoneId(e.target.value)}
              />
              <TextField
                label="Comentario"
                variant="standard"
                fullWidth
                multiline
                minRows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              mt={4}
              gap={2}
            >
              <Button
                variant="contained"
                onClick={handleClose}
                fullWidth
                color="primary"
              >
                CANCELAR
              </Button>
              <Button
                variant="contained"
                onClick={handleNextStep}
                fullWidth
                color="secondary"
              >
                SIGUIENTE
              </Button>
            </Box>
          </>
        )}

        {step === 2 && (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Haz click en el mapa para posicionar la estación.
              Click derecho sobre el marcador para eliminarlo.
            </Typography>
            <Box
              border="1px solid #e0e0e0"
              flex="1"
              minHeight="400px"
            >
              <MapWrapper>
                <GoogleMaps
                  farm={{
                    id: selectedFarm?.id ?? '',
                    coordinates: farmCoordinates,
                    zoom: selectedFarm?.zoom ?? 18,
                  }}
                  lat={selectedFarm?.center?.lat ?? ''}
                  lng={selectedFarm?.center?.lng ?? ''}
                  drawable="marker"
                  polygons={selectedFarm?.sectors ?? []}
                  markers={filteredDevices(
                    allFarmDevices,
                    sensorNodeFilter,
                  )}
                  setCoordinates={setCoordinates}
                />
              </MapWrapper>
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              mt={2}
              gap={2}
            >
              <Button
                variant="contained"
                onClick={() => {
                  setCoordinates(null);
                  setStep(1);
                }}
                fullWidth
                color="primary"
              >
                ANTERIOR
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                fullWidth
                color="secondary"
              >
                {loading ? 'AGREGANDO...' : 'AGREGAR'}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </CustomModal>
  );
};

export default AddWeatherStationModal;
